import {
  McpServiceAuthRequiredData,
  FileGenerationStatus,
  SearchSource,
} from 'features/chat/types/chat';

export enum SSEResponseTypes {
  CODE_EXECUTION_STARTED = 'CODE_EXECUTION_STARTED',
  CODE_EXECUTION_STEP = 'CODE_EXECUTION_STEP',
  CODE_EXECUTION_ARTIFACT = 'CODE_EXECUTION_ARTIFACT',
  LLM_ANSWER = 'LLM_ANSWER',
  SERVER_ERROR = 'SERVER_ERROR',
  TOKEN_ANALYTICS = 'TOKEN_ANALYTICS',
  CONVERSATION_TITLE = 'CONVERSATION_TITLE',
  END_OF_STREAM = 'END_OF_STREAM',
  LLM_THINKING = 'LLM_THINKING',
  FILE_GENERATION_ON_PROGRESS = 'FILE_GENERATION_ON_PROGRESS',
  IMAGE_GENERATION_STARTED = 'IMAGE_GENERATION_STARTED',
  IMAGE_GENERATION_SUCCESS = 'IMAGE_GENERATION_SUCCESS',
  IMAGE_GENERATION_FAILED = 'IMAGE_GENERATION_FAILED',
  CONTEXT_WINDOW_EXCEEDED = 'CONTEXT_WINDOW_EXCEEDED',
  IMAGE_GENERATION_LIMIT_EXCEEDED = 'IMAGE_GENERATION_LIMIT_EXCEEDED',
  FILE_GENERATION_STARTED = "FILE_GENERATION_STARTED",
  FILE_GENERATION_SUCCESS = "FILE_GENERATION_SUCCESS",
  FILE_GENERATION_FAILED = "FILE_GENERATION_FAILED",
  RESPONSE_SOURCES = "SOURCES",
  SEARCH_SOURCES = "SEARCH_SOURCES",
  SEARCH_RESPONSE = "SEARCH_RESPONSE",
  ASSISTANT_REQUEST_PROCESSING_STARTED = "ASSISTANT_REQUEST_PROCESSING_STARTED",
  ASSISTANT_REQUEST_QUEUED = "ASSISTANT_REQUEST_QUEUED",
  RESPONSE_TIME_ANALYTICS = "RESPONSE_TIME_ANALYTICS",
  MODEL_SELECTED = "MODEL_SELECTED",
  STREAM_CANCELLED = "STREAM_CANCELLED",
  /** Tool invoked during streaming (`streamingResponse` on ASSISTANT_STREAMED_RESPONSE). */
  TOOL_CALL_STARTED = "TOOL_CALL_STARTED",
  /** Tool finished during streaming (`streamingResponse` on ASSISTANT_STREAMED_RESPONSE). */
  TOOL_CALL_COMPLETED = "TOOL_CALL_COMPLETED",
  SKILL_USED = "SKILL_USED",
  SKILL_SKIPPED = "SKILL_SKIPPED",
  SKILL_RESOURCE_LOADED = "SKILL_RESOURCE_LOADED",
  /** Subagent delegation started (`streamingResponse` on ASSISTANT_STREAMED_RESPONSE). */
  SUBAGENT_STARTED = "SUBAGENT_STARTED",
  /** Subagent delegation finished (`streamingResponse` on ASSISTANT_STREAMED_RESPONSE). */
  SUBAGENT_COMPLETED = "SUBAGENT_COMPLETED",
  /** MCP Service (Swagger-derived MCP) requires per-user credentials before the turn can continue. */
  AUTH_REQUIRED = "auth_required",
  /** A2UI surface chunk (`streamingResponse` on ASSISTANT_STREAMED_RESPONSE). */
  A2UI_MESSAGE = "A2UI_MESSAGE",
  /** HIL approval interrupt: turn paused until the initiator approves/declines. */
  APPROVAL_REQUIRED = "APPROVAL_REQUIRED",
  /** HIL approval resolved (approved/declined/timeout). */
  APPROVAL_RESOLVED = "APPROVAL_RESOLVED",
  /** Conversation history compaction started (`streamingResponse` on ASSISTANT_STREAMED_RESPONSE). */
  CONVERSATION_COMPACTION_STARTED = "CONVERSATION_COMPACTION_STARTED",
  /** Conversation history compaction finished successfully. */
  CONVERSATION_COMPACTION_COMPLETED = "CONVERSATION_COMPACTION_COMPLETED",
  /** Conversation history compaction failed. */
  CONVERSATION_COMPACTION_FAILED = "CONVERSATION_COMPACTION_FAILED",
  /** Guardrail content filter violation chunk with refusal text. */
  CONTENT_FILTER_VIOLATION = "CONTENT_FILTER_VIOLATION",
  /** Platform resource created/updated/deleted through chat. */
  PLATFORM_RESOURCE_CHANGED = "PLATFORM_RESOURCE_CHANGED",
}

export interface GeneratedFile {
  file_id: string;
  file_name: string;
  file_size?: number | null;
  file_type: string;
  error?: string;
  status: FileGenerationStatus;
  message?: string;
}

export interface TokenAnalytics {
  completion_tokens: number;
  prompt_tokens: number;
  total_tokens: number;
}

export interface SourceFile {
  file_id: string;
  file_name: string;
  chunks: any[];
  uri?: string;
}

export interface ResponseTimeAnalytics {
  userId: string;
  orgId: string;
  tenantId: string;
  conversationId: string;
  messageId: string;
  pipelineStartedAt: string;
  pipelineEndedAt: string;
  hasUploadedFiles: boolean;
  allDetectedIntents: string[];
  timeToFirstTokenMs: number;
  totalPipelineDurationMs: number;
  error: string | null;
}

export interface ModelSelected {
  selected_model: string;
  detected_capability: string;
}

/** A2UI_MESSAGE chunk payload (wire contract §1, camelCase). */
export interface A2UIMessagePayload {
  version: string;
  surfaceId: string;
  /** "surface" (create/replace) | "update" (incremental). */
  kind: 'surface' | 'update';
  /** Raw A2UI spec messages (createSurface/updateComponents/updateDataModel/…). */
  messages: Record<string, unknown>[];
  purpose: 'generic' | 'selection' | 'approval' | 'dashboard';
}

/** APPROVAL_REQUIRED chunk payload (wire contract §3). */
export interface ApprovalRequiredPayload {
  approvalId: string;
  toolName: string;
  toolArgs?: Record<string, unknown>;
  agentId?: string;
  /** Delegating subagent, when the approval originates inside a subagent run. */
  subagentName?: string;
  initiatorUserId?: string;
  expiresAt?: string;
  /** Server-generated A2UI payload, purpose="approval". */
  surface?: A2UIMessagePayload;
}

/** APPROVAL_RESOLVED chunk payload (wire contract §3). */
export interface ApprovalResolvedPayload {
  approvalId: string;
  decision: 'approved' | 'declined';
  resolvedBy?: string;
}

/** PLATFORM_RESOURCE_CHANGED chunk payload (Agents V2 Resource-Change Events). */
export interface PlatformResourceChangedData {
  kind: 'agents' | 'kb_folders' | 'tools' | 'skills' | string;
  action: 'created' | 'updated' | 'deleted' | string;
  resourceId: string;
  name?: string;
  fields?: Record<string, any>;
}

export interface SSEResponse {
  data: string | TokenAnalytics | GeneratedFile | SourceFile[] | SearchSource[] | ResponseTimeAnalytics | ModelSelected | AssistantPositionRemainingType | McpServiceAuthRequiredData | A2UIMessagePayload | ApprovalRequiredPayload | ApprovalResolvedPayload | PlatformResourceChangedData;
  type: SSEResponseTypes;
  conversation_id: string;
  file_names?: string[];
  assistant_message_id?: string;
  message_sender_id?: string;
  parent_ids?: string[];
  /** ASSISTANT_STREAMED_RESPONSE.reason (e.g. NEW_CONNECTION_WHILE_STREAMING) */
  // Known values: NEW_CONNECTION_WHILE_STREAMING (reconnect replay marker),
  // 'approval_pending' (END_OF_STREAM of a turn paused for HIL approval — the
  // turn is parked awaiting approve/decline, not complete).
  stream_reason?: string;
  /** SERVER_ERROR sub-category (e.g. agent_unavailable) */
  category?: string;
  /** SERVER_ERROR sanitized message (the wire chunk's `error` field) */
  error?: string;
}

export interface AssistantPositionRemainingType {
  message: string;
  position: number;
  remaining: number;
}
